import Modal from "@/components/common/modal";
import { DialogFooter } from "@/components/ui/dialog";
import {
  OBSERVATION_MAX_LENGTH,
  observationReasonSchema,
  type ObservationReasonFormData,
} from "@/schemas/procedures/observation-reason";
import { Button, TextAreaField } from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect } from "react";

interface Props {
  isOpen: boolean;
  defaultReason?: string;
  onClose: () => void;
  onAccept: (val: { description: string }) => void;
  readOnly?: boolean;
}

const DenyReasonModal = ({
  isOpen,
  defaultReason,
  onClose,
  onAccept,
  readOnly = false,
}: Props) => {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ObservationReasonFormData>({
    resolver: zodResolver(observationReasonSchema),
    defaultValues: {
      reason: defaultReason || "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const [reason] = watch(["reason"]);

  // Actualizar el formulario cuando cambie defaultReason
  useEffect(() => {
    reset({
      reason: defaultReason || "",
    });
  }, [defaultReason, reset]);

  const onSubmit = async (data: ObservationReasonFormData) => {
    onAccept({
      description: data.reason,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={readOnly ? "Comentario de observación" : "Marcar como observación"}
      description={
        readOnly
          ? "Este es el comentario que se dejó al observar este campo."
          : "Escribe la observación de este campo para que el usuario pueda corregir sus datos."
      }
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!readOnly) {
            handleSubmit(onSubmit)(e);
          }
        }}
        id="edit-role-form"
      >
        <TextAreaField
          label={readOnly ? "Comentario" : "¿Por qué observas este campo?"}
          required={!readOnly}
          placeholder={readOnly ? "" : "Escribe tu comentario..."}
          className="relative min-h-[88px] max-h-[200px]"
          {...register("reason")}
          limit={OBSERVATION_MAX_LENGTH}
          count={reason?.length || 0}
          error={errors.reason?.message}
          disabled={readOnly}
        />
        <DialogFooter className="pt-1">
          {readOnly ? (
            <Button
              size="lg"
              type="button"
              className="w-full rounded-lg"
              variant="default"
              onClick={onClose}
            >
              Cerrar
            </Button>
          ) : (
            <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
              <Button
                size="lg"
                type="button"
                className="w-full rounded-lg"
                variant="tertiary"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                size="lg"
                variant="danger-secondary"
                className="w-full rounded-lg"
                type="submit"
                disabled={!isValid}
              >
                Aceptar
              </Button>
            </div>
          )}
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default DenyReasonModal;
